import React from 'react'
import { Star, CheckCircle2, TrendingUp } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

const formatDate = (isoString: string): string => {
  const date = new Date(isoString)
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

interface ReviewContentProps {
  mode?: string | null
  score: number
  createdAt: string
  mastered?: string | null
  toImprove?: string | null
  feedback: string
}

// Shared review body — meta chips, mastered/to-improve highlights, and full feedback markdown.
// Used by the Review page accordion (Module2Review) and the post-roleplay review message (Module2Chat).
const ReviewContent: React.FC<ReviewContentProps> = ({ mode, score, createdAt, mastered, toImprove, feedback }) => (
  <div className="flex flex-col gap-4">
    {/* Meta chips */}
    <div className="flex flex-wrap items-center gap-2">
      {mode && <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-[11px] font-medium text-primary">{mode}</span>}
      <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-100 bg-gray-50 px-3 py-1 text-[11px] font-medium text-gray-600">
        <Star size={12} className="fill-[#3D8C71] text-[#3D8C71]" />
        {score}/5
      </span>
      <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-100 bg-gray-50 px-3 py-1 text-[11px] font-medium text-gray-600">{formatDate(createdAt)}</span>
    </div>

    {/* Mastered / To improve highlights */}
    {(mastered || toImprove) && (
      <div className="grid gap-2 sm:grid-cols-2">
        {mastered && (
          <div className="rounded-lg border border-[#3D8C71]/20 bg-[#3D8C71]/5 px-3 py-2">
            <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-[#3D8C71] font-semibold mb-1">
              <CheckCircle2 size={12} />
              Mastered
            </p>
            <p className="text-sm text-gray-800 leading-relaxed">{mastered}</p>
          </div>
        )}
        {toImprove && (
          <div className="rounded-lg border border-amber-300/40 bg-amber-50 px-3 py-2">
            <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-amber-700 font-semibold mb-1">
              <TrendingUp size={12} />
              To Improve
            </p>
            <p className="text-sm text-gray-800 leading-relaxed">{toImprove}</p>
          </div>
        )}
      </div>
    )}

    {/* Full feedback message (markdown) */}
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-2">Feedback Details</h4>
      <div className="max-h-52 md:max-h-60 overflow-y-auto pr-1 custom-scrollbar">
        <div className="prose prose-sm max-w-none text-gray-800 leading-relaxed wrap-break-word">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{feedback}</ReactMarkdown>
        </div>
      </div>
    </div>
  </div>
)

export default ReviewContent
